import PropTypes from "prop-types";
import {
  Form,
  Label,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  FormFeedback,
  Alert,
} from "reactstrap";
// Formik
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import {
  inviteCompany,
  updateCompany,
  companyUpdateStatusState,
} from "../../store/actions";
import { COMPANY_INVITE_STATUS } from "../../store/company/actionType";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import Loader from "./Loader";
import "react-toastify/dist/ReactToastify.css";

const TenantModal = ({
  show,
  onCloseClick,
  mode = "create",
  existingData = null,
  tenantId = null,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    companyInviteStatus,
    companyInviteError,
    isCompanyUpdateLoading,
    companyUpdateError,
  } = useSelector((state) => ({
    companyInviteStatus: state.Company.companyInviteStatus,
    companyInviteError: state.Company.companyInviteError,
    isCompanyUpdateLoading: state.Company.isCompanyUpdateLoading,
    companyUpdateError: state.Company.companyUpdateError,
  }));

  const [newCustomerInfo, setNewCustomerInfo] = useState({
    tenantName: "",
    email: "",
  });

  const [isUpdating, setIsUpdating] = useState(false);

  // validation
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: {
      tenantName: existingData?.tenantName || "",
      email: existingData?.email || "",
    },
    validationSchema: Yup.object({
      tenantName: Yup.string().required(t("tenants.modal.tenantNameRequired")),
      email:
        mode === "edit"
          ? Yup.string() // Optional in edit mode
          : Yup.string()
              .matches(
                /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                t("tenants.modal.invalidEmail")
              )
              .required(t("tenants.modal.emailRequired")),
    }),
    onSubmit: (values) => {
      setNewCustomerInfo(values);

      if (mode === "edit" && tenantId) {
        // Edit mode: update tenant name only
        setIsUpdating(true);
        const payload = {
          tenantName: values.tenantName,
        };
        dispatch(updateCompany(tenantId, payload));
      } else {
        // Create mode: invite new tenant
        const payload = {
          inviteOnly: false,
          sendInviteEmail: true,
          email: values.email,
          tenantName: values.tenantName,
        };
        dispatch(inviteCompany(payload));
      }
    },
  });

  // Handle success - close modal and show toast
  useEffect(() => {
    if (companyInviteStatus === "success") {
      toast.success(
        t("tenants.modal.inviteSuccess", { email: newCustomerInfo.email }),
        {
          autoClose: 3000,
        }
      );
      dispatch(companyUpdateStatusState(COMPANY_INVITE_STATUS, "idle"));
      validation.resetForm();
      setNewCustomerInfo({ tenantName: "", email: "" });
      onCloseClick();
    }
  }, [companyInviteStatus]);

  // Handle update success
  useEffect(() => {
    if (isUpdating && !isCompanyUpdateLoading && !companyUpdateError) {
      toast.success(t("tenants.modal.updateSuccess"), {
        autoClose: 3000,
      });
      validation.resetForm();
      setNewCustomerInfo({ tenantName: "", email: "" });
      setIsUpdating(false);
      onCloseClick();
    } else if (isUpdating && !isCompanyUpdateLoading && companyUpdateError) {
      // Update failed, just reset the flag
      setIsUpdating(false);
    }
  }, [isCompanyUpdateLoading, companyUpdateError, isUpdating]);

  const handleModalToggle = () => {
    onCloseClick();
    validation.resetForm();
  };

  return (
    <Modal
      isOpen={show}
      toggle={handleModalToggle}
      centered={true}
      className="new-client-modal"
    >
      <ModalHeader toggle={handleModalToggle} className="border-0 pb-0">
        <h4
          className="mb-2"
          style={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "20px",
            fontWeight: 600,
            color: "#063a5e",
            lineHeight: "1.3",
          }}
        >
          {mode === "edit"
            ? t("tenants.modal.editTitle")
            : t("tenants.modal.createTitle")}
        </h4>
      </ModalHeader>
      <ModalBody className="pt-0 pb-4 px-5">
        <Form
          action="#"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          {/* Header Section */}
          <div className="mb-4 text-center">
            <p
              className="mb-0"
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                color: "#757575",
                lineHeight: "1.5",
              }}
            >
              {mode === "edit"
                ? t("tenants.modal.editDescription")
                : t("tenants.modal.createDescription")}
            </p>
          </div>

          {(companyInviteStatus === "failed" || companyUpdateError) && (
            <Alert color="danger" className="text-center fw-semibold">
              {mode === "edit" ? companyUpdateError : companyInviteError}
            </Alert>
          )}

          {/* Tenant Name Field */}
          <div className="mb-3">
            <Label
              for="tenantName"
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                color: "#616161",
                lineHeight: "1.2",
              }}
            >
              {t("tenants.modal.tenantName")}{" "}
              <span style={{ color: "#e94a53" }}>*</span>
            </Label>
            <Input
              name="tenantName"
              placeholder=""
              type="text"
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "16px",
                height: "40px",
                borderColor: "#e0e0e0",
                borderRadius: "4px",
              }}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.tenantName || ""}
              invalid={
                validation.touched.tenantName && validation.errors.tenantName
              }
            />
            {validation.touched.tenantName && validation.errors.tenantName && (
              <FormFeedback>{validation.errors.tenantName}</FormFeedback>
            )}
          </div>

          {/* Email Field - Only show in create mode */}
          {mode === "create" && (
            <div className="mb-4">
              <Label
                for="email"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#616161",
                  lineHeight: "1.2",
                }}
              >
                {t("common.labels.email")}{" "}
                <span style={{ color: "#e94a53" }}>*</span>
              </Label>
              <Input
                name="email"
                placeholder=""
                type="email"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "16px",
                  height: "40px",
                  borderColor: "#e0e0e0",
                  borderRadius: "4px",
                }}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.email || ""}
                invalid={validation.touched.email && validation.errors.email}
              />
              {validation.touched.email && validation.errors.email && (
                <FormFeedback>{validation.errors.email}</FormFeedback>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="d-flex flex-column gap-2">
            <Button
              type="submit"
              disabled={
                mode === "edit"
                  ? isCompanyUpdateLoading
                  : companyInviteStatus === "loading"
              }
              className="w-100"
              color="primary"
            >
              {(
                mode === "edit"
                  ? isCompanyUpdateLoading
                  : companyInviteStatus === "loading"
              ) ? (
                <>
                  <Loader color="light" size="sm"></Loader>
                  <span className="ms-2">
                    {mode === "edit"
                      ? t("tenants.modal.updating")
                      : t("tenants.modal.sending")}
                  </span>
                </>
              ) : mode === "edit" ? (
                t("tenants.modal.updateTenant")
              ) : (
                t("tenants.modal.sendInvitation")
              )}
            </Button>
            <Button
              type="button"
              onClick={handleModalToggle}
              disabled={
                mode === "edit"
                  ? isCompanyUpdateLoading
                  : companyInviteStatus === "loading"
              }
              className="w-100"
              color="tertiary"
            >
              {t("common.actions.cancel")}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

TenantModal.propTypes = {
  onCloseClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  show: PropTypes.bool,
  mode: PropTypes.oneOf(["create", "edit"]),
  existingData: PropTypes.shape({
    tenantName: PropTypes.string,
    email: PropTypes.string,
  }),
  tenantId: PropTypes.string,
};

export default TenantModal;
